import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractComponent } from '@sol/components/components/abstract.component';
import { TimerTools } from '@sol/components/tools/timer-tools';
import { PopoverService, SolPopover } from '@sol/components/services/popover.service';
import { HostStyle } from '@sol/components/components/popover/sol-popover/sol-popover.state';
import { LabPopoverState } from './lab-popover.state';

@Component({
  selector: 'lab-popover',
  standalone: true,
  imports: [],
  templateUrl: './lab-popover.component.html',
  styleUrl: './lab-popover.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LabPopoverComponent extends AbstractComponent<LabPopoverState> implements AfterViewInit, OnDestroy {
  // Dependencies
  private readonly popoverService = inject(PopoverService);

  // Computed
  protected readonly hostStyle$ = this.compute(state => state.hostStyle);
  protected readonly fixed$ = this.compute(state => state.fixed);
  protected readonly containerClass$ = this.compute(state => state.containerClass);
  protected readonly popoverClass$ = this.compute(state => state.popoverClass);
  protected readonly popoverVisible$ = this.compute(state => state.popoverVisible);
  protected readonly popoverStyle$ = this.compute(state => state.popoverStyle);
  protected readonly contentStyle$ = this.compute(state => state.contentStyle);

  // Binding
  @ViewChild('container')
  private containerRef?: ElementRef;
  @ViewChild('popoverContent', { read: ViewContainerRef })
  private popoverContentRef?: ViewContainerRef;
  private resizeObserver?: ResizeObserver;

  constructor() {
    super(LabPopoverState.of());

    this.watchComputed(
      state => state.popoverVisible,
      popoverVisible => {
        if (popoverVisible) {
          TimerTools.nextTick(() => this.observeResize());
        } else {
          this.cleanUpResize();
        }
      },
    );
    this.watchComputed(
      state => state.data?.loadComponent,
      () => this.updatePopoverComponent(),
    );
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const data = this.state.data;
    const id = this.state.data?.id;
    if (!data || !id) {
      return;
    }

    const clickedInside = this.containerRef?.nativeElement.contains(event.target);
    if (clickedInside) {
      return;
    }

    this.popoverService.clickOutside(id);
  }

  @HostBinding('style')
  public get hostStyle(): HostStyle {
    return this.hostStyle$();
  }

  ngAfterViewInit(): void {
    this.updatePopoverComponent();
  }

  ngOnDestroy(): void {
    this.cleanUpResize();
  }

  @Input()
  public set data(data: SolPopover<any> | undefined) {
    this.update(state => state.withData(data));
  }

  protected onMouseEnter() {
    this.update(state => state.withHover(true));
  }

  protected onMouseLeave() {
    this.update(state => state.withHover(false));
  }

  private observeResize() {
    if (this.containerRef?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        if (!this.containerRef?.nativeElement) {
          return;
        }
        const popoverRect = this.containerRef!.nativeElement.getBoundingClientRect();

        this.update(state =>
          state.withResize(
            popoverRect.width,
            popoverRect.height,
            document.documentElement.clientWidth,
            document.documentElement.clientHeight,
          ),
        );
      });
      this.resizeObserver.observe(this.containerRef.nativeElement);
    }
  }

  private cleanUpResize() {
    this.resizeObserver?.disconnect();
  }

  private updatePopoverComponent(): void {
    const data = this.state.data;
    if (!data || !this.popoverContentRef) {
      return;
    }

    if (this.popoverContentRef.length > 0) {
      return;
    }

    data.loadComponent.then(componentType => {
      const componentRef: ComponentRef<AbstractComponent<any>> = this.popoverContentRef!.createComponent(componentType);
      data.configurer(componentRef, data.id);
      PopoverService.registerInstance(data.id, componentRef.instance);
      TimerTools.nextTick(() => componentRef.instance.focus());
    });
  }
}
